/**
 * components/workspace/CoverLetterSection.jsx — RC 1.49.7 Phase 2
 *
 * Live Cover Letter section for the route-based Application
 * Workspace. Mirrors the wizard's `Step3Letter` contract verbatim —
 * same endpoint, same idempotency key minting, same in-flight
 * recovery marker, same three-control footer.
 *
 * Hard rules (operator-locked, RC 1.49.x + RC 1.49.7):
 *
 *   • RC 1.49.x.RUN_ISOLATION (operator 2026-05-06): NEVER pre-
 *     hydrate a historical cover letter on mount. The user must
 *     explicitly click Generate. The ONE allowed exception is the
 *     in-flight letter marker recovery branch (the marker is
 *     written by the user's own Generate click in this session).
 *
 *   • RC 1.49.7 Phase 2 belt-and-braces job-scoping (operator
 *     2026-02): any recovered letter MUST match
 *       (a) marker key `(user_id, job_id)` — implicit
 *       (b) letter.base_cv_id === current primaryCv.id
 *       (c) letter.cv_variant_id === current carry-forward
 *           (`null` when Source CV is selected; specific id when
 *           a tailored variant is selected)
 *       (d) letter.content_json.idempotency_key === current
 *           cached idempotency key, when one exists.
 *     A letter failing ANY guard is discarded and the marker
 *     cleared. The workspace MUST NEVER prefill a letter from
 *     another job, or one written against a different
 *     Source CV / variant.
 *
 *   • RC 1.49.1 P0-FIN: financial idempotency key minted via the
 *     SAME `getOrMintIdempotencyKey(user, job, 'cover_letter')`
 *     helper as the wizard. Action-scoped — never collides with
 *     the CV `'cv_variant'` key for the same `(user, job)`.
 *
 *   • RC 1.49.1 P0-A: long-running LLM call wrapped in
 *     `withTimeoutRecovery({ kind: 'letter', ... })` so a 30s
 *     ingress proxy 504 surfaces a calm recovery toast instead
 *     of a double-charge attempt.
 *
 *   • RC 1.49.5A P0-A3: chargeable button hard-disabled while
 *     `creditsLoading` is true.
 *
 *   • RC 1.49.7 Phase 2 (operator 2026-02) Workspace UI rule —
 *     no floating panels (no `<Sheet>`, no `<Dialog>`, no
 *     `<Drawer>`, no `<Popover>`, no second Generate button).
 *     ONE primary action button per section. Any future options
 *     live inline above the footer.
 *
 *   • Generate ≠ Continue — three separate controls. The user may
 *     SKIP cover-letter generation entirely.
 *
 *   • Pricing label: ALWAYS sourced from
 *     `getActionCost(usageCosts, 'cover_letter')`, never hard-coded.
 *
 *   • In-place letter editing + autosave: deferred to a Phase 2
 *     polish (operator 2026-02). Phase 2 ships generate / display /
 *     skip / continue only.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Sparkles, Loader2, Mail, CheckCircle2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiFetch } from '@/lib/api';
import {
  useCredits, getActionCost,
} from '@/lib/creditsContext';
import { handleCreditsError } from '@/lib/handleCreditsError';
// RC 1.49.7 Slice 6b — invalidate the cover-letter list cache
// after a successful generate so library / inbox views surface
// the new draft without waiting for the 180-second TTL. Wizard
// parity (`JobApplicationWizard.jsx:2845`).
import { invalidate as cacheInvalidate } from '@/lib/listCache';
import {
  getOrMintIdempotencyKey,
  clearIdempotencyKey,
  peekIdempotencyKey,
} from '@/lib/idempotencyKeys';
import {
  writeInflightLetterMarker,
  patchInflightLetterMarker,
  readInflightLetterMarker,
  clearInflightLetterMarker,
  isInflightLetterMarkerExpired,
  findLetterByTitleStamp,
} from '@/lib/inflightLetterRecovery';
import { generateCoverLetterWithPolling, pollCoverLetterRun } from '@/lib/coverLetterPolling';

// RC 1.53 Phase 4.3A — controller wiring.
// The cover letter section now dispatches through the
// OperationLifecycleController. The actual LLM call still goes
// through `generateCoverLetterWithPolling` (via the adapter); the
// controller gives us:
//   • no-op-on-in-flight guard
//   • `STABILIZING` post-success status so the activity indicator
//     stays visible until cache invalidation + credits.refresh
//     have landed (first visible, last gone)
//   • workspace-wide Generate-lock aggregation
//   • cross-section operation:complete event (the Pack section can
//     subscribe to `pack:artefact_ready` to refresh its readiness
//     state without a manual user click)
import {
  dispatch as controllerDispatch,
  emit as controllerEmit,
} from '@/lib/operationController';
import { useOperationLifecycle } from '@/lib/operationController/useOperationLifecycle';
import { OPERATION_EVENT_TYPES } from '@/lib/operationController/operationEvents';
import * as coverLetterAdapter from '@/lib/operationController/adapters/coverLetterAdapter';
import { trace as actionTrace } from '@/lib/operationController/actionTrace';
import { verifyAndHydrateCoverLetter } from '@/lib/operationController/postTerminalHydrator';
import { useArtefactRehydrate } from '@/lib/operationController/useArtefactRehydrate';
import WorkspaceActivityIndicator from '@/components/workspace/WorkspaceActivityIndicator';
// ── Bundle U-9.G — Loaded-from-history chip (2026-02-15) ────────
import HistoryBadge from '@/components/workspace/HistoryBadge';

// ─── RC 1.52 Phase 3A.DUP — system-name minting via shared helper ──
// Cover Letter titles are SYSTEM-generated and collision-proof
// ({Company} Cover Letter — YYYY-MM-DD HH:MM:SS.mmm — XXXX). The
// user does NOT rename generated Cover Letters in the UI.
import { mintCoverLetterTitle } from '@/lib/titleStamps';

// First N lines of the letter draft for the calm preview card.
const PREVIEW_LINES = 6;

export default function CoverLetterSection({
  job,
  jobId,
  primaryCv,
  onContinue,
  onBack,
  onBusyChange,
  // RC 1.53 Phase 4.2.1.1.d — workspace-wide Generate-lock.
  workspaceBusy = false,
  // ── Bundle U-9.B props (2026-02-15) ─────────────────────────────
  lookupStatus = 'ready',
  loadedFromHistory = false,
  rootTestId = 'workspace-letter-section',
}) {
  const credits = useCredits();
  const usageCosts = credits?.usageCosts;
  const creditsLoading = !credits || !!credits.loading;

  const letterCost = getActionCost(usageCosts, 'cover_letter');
  const letterCostLabel = letterCost == null
    ? null
    : `${letterCost} ${letterCost === 1 ? 'Credit' : 'Credits'}`;

  // ── CV carry-forward (READ-ONLY here) ────────────────────────────
  // CvSection writes `?source_cv_id` + `?source_cv_type` on selection
  // and after generate / recovery. We READ them here. Falls back to
  // primaryCv when params are absent (the user reached the letter
  // section without ever touching the CV picker — equivalent to
  // wizard's "Skip Generate CV" path).
  //
  // RC 1.49.7 Phase 4 (operator 2026-02): on a successful letter
  // generate / recovery, ALSO write `?cover_letter_id` to the URL
  // so the Pack section can pick it up via the same URL-state
  // pattern. Phase 4's PackSection reads `cover_letter_id` from
  // here.
  const [searchParams, setSearchParams] = useSearchParams();
  const sourceCvId = searchParams.get('source_cv_id') || primaryCv?.id || null;
  const sourceCvType = searchParams.get('source_cv_type') || 'base_cv';
  const cvVariantId = sourceCvType === 'cv_variant' ? sourceCvId : null;

  // ── Letter state ─────────────────────────────────────────────────
  const [letterId, setLetterId] = useState(null);
  const [letterDraft, setLetterDraft] = useState('');
  const [letterTitle, setLetterTitle] = useState('');
  const [generating, setGenerating] = useState(false);
  // RC 1.50 Workspace UX Slice 0 — emit busy state to parent.
  useEffect(() => {
    onBusyChange?.(generating);
  }, [generating, onBusyChange]);
  const [recoveredFromInflight, setRecoveredFromInflight] = useState(false);

  // RC 1.53 Phase 4.3A — controller lifecycle subscription.
  // Drives the activity indicator + Generate-button gating across
  // `dispatching → polling → stabilizing → complete`. Section's own
  // `generating` boolean still owns its local UI logic.
  const {
    status: controllerStatus,
    markComplete: lifecycleMarkComplete,
    markFailed:   lifecycleMarkFailed,
  } = useOperationLifecycle(jobId, 'cover_letter');
  // RC 1.53 Phase 4.3A — progress_label auxiliary state for the
  // activity indicator (mirrors CvSection's `progressLabel` state).
  const [progressLabel, setProgressLabel] = useState(null);
  // RC 1.49.7 Slice 3 — Billing transparency (operator P0, 2026-02).
  // Stores the authoritative `billing` block from the post-fix
  // `/api/cover-letters/generate` response (RC 1.49.7
  // PHASE5-PRECHECK COVER-LETTER BILLING). Render-only — never
  // derived client-side, never falls back to 0 when absent. Per
  // billing_contract.md §6.1.
  const [letterBilling, setLetterBilling] = useState(null);
  const [letterChargeReason, setLetterChargeReason] = useState(null);
  // U-13.AUDIT (2026-02-15) — capture the bound CL row's
  // `cv_variant_id` + `base_cv_id` so operator audits can verify
  // (via DOM attrs on the rendered card) that the bound letter
  // truly matches the selected CV context.
  const [letterRowCvVariantId, setLetterRowCvVariantId] = useState(null);
  const [letterRowBaseCvId,    setLetterRowBaseCvId]    = useState(null);
  // RC 1.49.7 Layer-A* UX (operator 2026-02-09 PM) — inline
  // "Restoring your cover letter…" indicator while the URL-driven
  // mount-rehydrate (Slice 6c) is in flight. Cleared in finally
  // regardless of cancellation (PackSection-parity).
  const [restoring, setRestoring] = useState(false);
  // RC 1.55 P0 (2026-05-12 PM) — calm post-terminal hydration-pending
  // surface. When the post-terminal retry schedule
  // [0,750,1500,3000] ms exhausts (i.e. the row is committed and the
  // charge has succeeded but the canonical GET is still returning an
  // incomplete row), we surface a calm "Generated and saved.
  // Refreshing preview…" banner with a Retry button that re-runs the
  // canonical READ-ONLY GET (never `/api/runs`, never re-charges).
  // `null` when no pending hydration; otherwise `{ letterId }`.
  const [pendingHydration, setPendingHydration] = useState(null);
  const [retryingHydration, setRetryingHydration] = useState(false);
  // ── U-6.CL — Recovering surface (2026-02-15) ─────────────────────
  //
  // OPERATOR-LOCKED. Symmetric to InterviewPrepSection.jsx's
  // `recovering` state. Flipped ON in the `err.recoverable===true`
  // catch arm (L867 area) so the U-4-style live binder below knows
  // it should keep polling the canonical by-job endpoint on each
  // visibilitychange / heartbeat. Cleared when the binder lands a
  // terminal artefact OR the section unmounts.
  const [recovering, setRecovering] = useState(false);

  // ── Bundle U-9.E — CL regenerate confirm gate state (2026-02-15) ─
  //
  // OPERATOR-LOCKED. Symmetric to U-3.IP (InterviewPrepSection.jsx)
  // and U-5.CV.A (CvSection.jsx). When the section already has a
  // bound letter and the user clicks Generate, the click opens an
  // inline confirm dialog instead of dispatching. "Keep existing"
  // dismisses without dispatch. "Regenerate cover letter" clears
  // the idempotency key for this (user, job, 'cover_letter') tuple
  // and dispatches a fresh letter. First-time generation (no
  // letter bound) bypasses the dialog.
  //
  // Shape: `null` (gate closed) | `{ source: 'cl_generate' }`.
  const [regenerateConfirm, setRegenerateConfirm] = useState(null);

  // ── URL-write side-effect: mirror letterId into URL params ───────
  // RC 1.49.7 Phase 4 (operator 2026-02): when a letter is generated
  // OR recovered, write `?cover_letter_id` so the Pack section can
  // bind it without a prop drill. `replace: true` so we don't add a
  // history entry for the side-effect. Same pattern CvSection uses
  // for `?source_cv_id` / `?source_cv_type`.
  useEffect(() => {
    if (!letterId) return;
    const next = new URLSearchParams(searchParams);
    if (next.get('cover_letter_id') === letterId) return;
    next.set('cover_letter_id', letterId);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [letterId]);

  // ── RC 1.49.7 Slice 6c — URL-bound mount rehydration ─────────────
  //
  // Pre-Slice-6c the Letter section's only mount-time hydration was
  // the in-flight-marker recovery (covers a refresh DURING a generate
  // POST). After a successful generate the marker is cleared
  // (`clearInflightLetterMarker(...)`), so a hard refresh on the
  // post-success state showed the empty "Generate Cover Letter"
  // button — looking exactly like a charge-with-no-output bug.
  // CvSection has the parity fix (`CvSection.jsx:508-573`); this is
  // its mirror for Letter.
  //
  // Rehydration source: `GET /api/cover-letters/:id` (returns the
  // raw `application_documents` row). The endpoint already enforces
  // user ownership (`user_id=eq.{user_id}`), so we don't need a
  // separate auth check here.
  //
  // Strict source-match rails (operator-locked, no exceptions):
  //   • `metadata.base_cv_id` MUST equal the current primaryCv.id.
  //   • `metadata.cv_variant_id` MUST equal `cvVariantId`
  //     (i.e. `null` when current source is Source CV; the variant
  //     UUID when current source is a variant).
  //   • If EITHER guard fails, we DO NOT bind the letter, DO NOT
  //     toast, DO NOT regenerate, and DO NOT clear the URL param —
  //     PackSection's own `_validate_pack_refs` will surface the
  //     mismatch at create time.
  //
  // No regenerate. No duplicate charge. No floating UI. Render-only.
  // The effect runs ONCE per (jobId, primaryCv.id, cvVariantId,
  // urlLetterId) tuple — if the user changes source CV mid-flow the
  // existing CvSection effect clears `cover_letter_id` from the URL
  // (Slice 5b parity), so this effect immediately stops firing for
  // the stale id.
  const urlLetterId = searchParams.get('cover_letter_id');

  // ──────────────────────────────────────────────────────────────
  // RC 1.55 U1 (operator-locked, 2026-05-12 PM) — canonical
  // fetch-and-bind, extracted into a stable callback so it can be
  // driven by BOTH the existing mount-time URL effect (below) AND
  // the new `useArtefactRehydrate` hook (visibility / popstate).
  //
  // Logic is byte-equivalent to the prior URL effect — only the
  // shape changed. READ-ONLY GET. No charge. No dispatch. Honour
  // every prior alignment guard.
  // ──────────────────────────────────────────────────────────────
  const refetchCoverLetterFromUrl = useCallback(async () => {
    if (!urlLetterId) return;
    if (!jobId || !primaryCv?.id) return;
    // Already bound — nothing to do.
    if (letterId === urlLetterId) return;
    // A generate is mid-flight or just finished — let the in-flight
    // recovery / success path own the binding; we'd race them
    // otherwise.
    if (generating || recoveredFromInflight) return;

    setRestoring(true);
    try {
      const row = await apiFetch(`/api/cover-letters/${urlLetterId}`);
      if (!row) return;
      // RC 1.49.7 Slice 6c HOTFIX (operator 2026-02 soak) —
      // application_documents row carries base_cv_id and
      // cv_variant_id as TOP-LEVEL columns. See the prior
      // historical comment block.
      const meta = (row.metadata && typeof row.metadata === 'object')
        ? row.metadata
        : (row.content_json && typeof row.content_json === 'object'
          ? row.content_json
          : {});
      const rowBaseCvId = row.base_cv_id ?? null;
      const rowVariantId = row.cv_variant_id ?? null;
      // Hard rail: enforce semantic alignment with the current
      // workspace source. A letter tailored for a different
      // primary CV or a different variant is INVISIBLE.
      //
      // U-13.CL (2026-02-15) — pack-mode source tolerance.
      // When the workspace was opened via Pack→Workspace
      // (`?from_pack=1`), the URL carries the pack's explicit
      // composition (cover_letter_id, interview_prep_id,
      // last_variant_id, source_cv_id). The pack's composition is
      // the authoritative intent — bind the exact letter the pack
      // stored even if it does not match the URL's source_cv_*
      // params (e.g., a future pack that intentionally pairs a
      // CL from variant-A with a CV from variant-B). The U-3.IP
      // / U-5.CV / U-9.E confirm gates already cover the
      // "regenerate against a different context" intent.
      const _fromPack = searchParams.get('from_pack') !== null;
      if (!_fromPack) {
        if (rowBaseCvId && rowBaseCvId !== primaryCv.id) return;
        if (rowVariantId !== cvVariantId) return;
      } else {
        // Pack mode: still refuse a cross-user row, but tolerate
        // a CV-context drift between the pack's composition and
        // the URL source params.
        if (rowBaseCvId && rowBaseCvId !== primaryCv.id) return;
      }
      if (row.job_id && row.job_id !== jobId) return;

      setLetterId(row.id);
      setLetterTitle(row.title || '');
      setLetterDraft(row.content_markdown || row.generated_text || '');
      // U-13.AUDIT — capture the bound row's context fingerprint.
      setLetterRowCvVariantId(rowVariantId);
      setLetterRowBaseCvId(rowBaseCvId);
      // Surface the historical billing block + charge reason if
      // the row carried them on the original generate response.
      if (meta.billing && typeof meta.billing === 'object') {
        setLetterBilling(meta.billing);
      }
      if (typeof meta.charge_reason === 'string') {
        setLetterChargeReason(meta.charge_reason);
      }
      // RC 1.55 Phase 1 (operator-locked, 2026-05-13) — terminal
      // side-effects on successful new canonical rebind. The
      // dispatch path normally fires these (L705-L737), but if
      // the dispatch was interrupted (tab close, navigation,
      // bfcache restore) and only the visibility/popstate-driven
      // rehydrate completes the binding, those side-effects are
      // lost and the wallet pill / Documents list / Pack badge
      // remain stale. Mirror them here on the SUCCESS branch
      // (after the alignment guards have passed) so every path
      // that binds a new artefact id leaves the same observable
      // post-condition.
      //
      // Hard rails (preserved from U1 contract):
      //   • READ-ONLY surface — no new POST, no generation
      //     re-dispatch, no charge-settlement re-call, no
      //     idempotency mutation.
      //   • Fail-open — every call is wrapped so a single failure
      //     never blocks the others.
      //   • Skipped by the "already bound" short-circuit above
      //     (L273) — no refresh spam on visibility flicker.
      try { if (typeof credits?.refresh === 'function') credits.refresh(); }
      catch { /* noop */ }
      try { cacheInvalidate(['cover-letters:all', 'dashboard:bundle']); } catch { /* noop */ }
      try {
        controllerEmit(OPERATION_EVENT_TYPES.PACK_ARTEFACT_READY, {
          jobId,
          actionCode: 'cover_letter',
          artefactId: row.id,
          reused: true,
        });
      } catch { /* noop */ }
    } catch {
      // Fail-open. The user can still click Generate (idempotent
      // on the backend via `idempotency_key`).
    } finally {
      setRestoring(false);
    }
  }, [
    urlLetterId, jobId, primaryCv?.id, cvVariantId,
    letterId, generating, recoveredFromInflight,
  ]);

  // Mount-time URL effect — unchanged contract, now delegates to the
  // shared callback. Runs once per dependency change.
  useEffect(() => {
    if (!urlLetterId) return undefined;
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await refetchCoverLetterFromUrl();
    })();
    return () => { cancelled = true; };
  }, [refetchCoverLetterFromUrl, urlLetterId]);

  // RC 1.55 U1 — visibility / popstate-driven re-fetch. The hook
  // installs `visibilitychange` + `popstate` listeners and calls
  // `refetchCoverLetterFromUrl()` on tab-return or browser
  // back/forward. Gated off while a generate is in-flight so the
  // dispatch's own binding path always wins. READ-ONLY. No POST.
  useArtefactRehydrate({
    enabled: !!urlLetterId
      && !!jobId
      && !!primaryCv?.id
      && !generating
      && !recoveredFromInflight,
    fetchAndBind: refetchCoverLetterFromUrl,
  });

  // ── U-6.CL.B — Live post-terminal binder (2026-02-15) ────────────
  //
  // OPERATOR-LOCKED. Symmetric to U-4.IP. Closes the live-return
  // binding gap on Cover Letter: when the polling helper raises
  // `recoverable=true` after the 120 s hard cap (tab-switch case),
  // the recoverable catch (L867 area) toasts and returns WITHOUT
  // calling setLetterDraft/setLetterId. Pre-U-6 the section stayed
  // empty until either (a) a hard refresh fired the in-flight
  // recovery effect, or (b) the user navigated away + back firing
  // the URL-bound rehydrate. With U-6 a long-lived watcher polls
  // the new canonical `GET /api/cover-letters/by-job/{jobId}?
  // latest=true` endpoint on visibilitychange + 12 s heartbeat
  // and re-binds via the existing `verifyAndHydrateCoverLetter`
  // chain — identical to U-4.IP's design.
  //
  // READ-ONLY. NEVER dispatches. NEVER charges. Stops itself the
  // moment `letterId` is bound. Activates only when there's reason
  // to believe a run is (or was) in flight: marker present OR
  // recovering surface ON OR generating surface ON.
  useEffect(() => {
    if (!jobId || !primaryCv?.user_id) return undefined;
    if (letterId) return undefined;

    let _hasMarker = false;
    try {
      const m = readInflightLetterMarker(primaryCv.user_id, jobId);
      _hasMarker = !!(m && !isInflightLetterMarkerExpired(m));
    } catch { /* noop */ }
    if (!_hasMarker && !recovering && !generating) return undefined;

    let cancelled = false;
    let timer = null;
    let lock = false;
    const HEARTBEAT_MS = 12000;

    async function tryBind() {
      if (cancelled || lock) return;
      lock = true;
      try {
        // U-12.FE.C — source-context filter (2026-02-15).
        // Honour the user's CV selection on the by-job lookup so
        // we never rebind a CL tied to a different variant.
        const _ctxQs = new URLSearchParams();
        _ctxQs.set('latest', 'true');
        if (sourceCvType === 'cv_variant' && sourceCvId) {
          _ctxQs.set('cv_variant_id', sourceCvId);
        } else if (sourceCvType === 'base_cv' && sourceCvId) {
          _ctxQs.set('base_cv_id', sourceCvId);
        }
        // Step 1 — canonical by-job lookup. Read-only; never charges.
        const env = await apiFetch(
          `/api/cover-letters/by-job/${encodeURIComponent(jobId)}?${_ctxQs.toString()}`,
        );
        if (cancelled || !env || env.found !== true) return;
        const clId = env.cover_letter_id;
        if (!clId) return;

        // Step 2 — full canonical hydrate via the same chain the
        // dispatch success path uses. The endpoint returns the
        // canonical row body INCLUDING `content_markdown`; verifyAnd-
        // HydrateCoverLetter will short-circuit if content already
        // populated, else retry-then-fail-open.
        let hydratedRow;
        try {
          hydratedRow = await verifyAndHydrateCoverLetter({
            envelope: {
              cover_letter_id:  clId,
              generated_text:   env.content_markdown || null,
              content_markdown: env.content_markdown || null,
              title:            env.title || null,
            },
            letterId: clId,
            apiGet:   (p, opts) => apiFetch(p, opts),
          });
        } catch (hydrateErr) {
          if (hydrateErr?.recoverable === true) return;
          actionTrace('cover_letter', 'live_binder_hydrate_failed', {
            letterId: clId, code: hydrateErr?.code,
          });
          return;
        }
        if (cancelled) return;
        const content =
          hydratedRow?.generated_text
          || hydratedRow?.content_markdown
          || env.content_markdown
          || '';
        if (!content) return; // Row not fully readable yet — retry.

        // Step 3 — bind via the canonical setter chain. Mirrors the
        // dispatch success branch (L780-810 area).
        setLetterId(clId);
        setLetterDraft(content);
        setLetterTitle(env.title || hydratedRow?.title || '');
        // U-13.AUDIT — capture row context fingerprint from the
        // by-job envelope so the DOM attrs reflect what the live
        // binder bound (operator audit visibility).
        setLetterRowCvVariantId(env.cv_variant_id ?? hydratedRow?.cv_variant_id ?? null);
        setLetterRowBaseCvId(env.base_cv_id ?? hydratedRow?.base_cv_id ?? null);
        setPendingHydration(null);
        setRecoveredFromInflight(false);
        try {
          const _next = new URLSearchParams(searchParams);
          if (_next.get('cover_letter_id') !== clId) {
            _next.set('cover_letter_id', clId);
            if (typeof setSearchParams === 'function') {
              setSearchParams(_next, { replace: true });
            }
          }
        } catch { /* noop */ }

        // Side-effects parity.
        try {
          clearInflightLetterMarker(primaryCv.user_id, jobId);
          clearIdempotencyKey(primaryCv.user_id, jobId, 'cover_letter');
        } catch { /* noop */ }
        try { setRecovering(false); } catch { /* noop */ }
        try { if (typeof credits?.refresh === 'function') credits.refresh(); }
        catch { /* noop */ }
        try { cacheInvalidate(['cover-letters:all', 'dashboard:bundle']); }
        catch { /* noop */ }
        try {
          controllerEmit(OPERATION_EVENT_TYPES.PACK_ARTEFACT_READY, {
            jobId,
            actionCode: 'cover_letter',
            artefactId: clId,
            reused: true,
          });
        } catch { /* noop */ }
        try {
          lifecycleMarkComplete({ letterId: clId, reused: true });
        } catch { /* noop */ }
        toast.success('Cover letter ready.');
        actionTrace('cover_letter', 'live_binder_bound', { letterId: clId });
      } catch (err) {
        actionTrace('cover_letter', 'live_binder_check_failed', {
          code: err?.code, status: err?.status,
        });
      } finally {
        lock = false;
      }
    }

    function onVisible() {
      if (typeof document !== 'undefined'
          && document.visibilityState === 'visible') {
        if (timer) clearTimeout(timer);
        timer = setTimeout(tryBind, 250);
      }
    }

    tryBind();
    const heartbeat = setInterval(() => {
      if (typeof document === 'undefined'
          || document.visibilityState === 'visible') {
        tryBind();
      }
    }, HEARTBEAT_MS);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisible);
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      clearInterval(heartbeat);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    jobId,
    primaryCv?.user_id,
    letterId,
    recovering,
    generating,
  ]);

  // ── In-flight recovery (the ONLY allowed mount-time hydration) ──
  //
  // Scope: marker key `inflight-letter:${userId}:${jobId}` is already
  // tied to the current (user, job). Belt-and-braces guards below
  // additionally verify base_cv_id / cv_variant_id / idempotency_key
  // BEFORE binding so a stale row belonging to a different (job,
  // source CV, attempt) can never be surfaced even if the title
  // happened to match.
  // RC 1.49.7 C6/G3 (operator 2026-02) — `recoveryAttemptedRef`
  // dropped to be StrictMode-safe (the boolean flip-before-fetch
  // pattern races with cleanup-cancellation in dev). The `cancelled`
  // flag + dep-array equality keep this idempotent without needing
  // the ref.
  useEffect(() => {
    if (!jobId || !primaryCv?.user_id || !primaryCv?.id) return undefined;

    let cancelled = false;
    (async () => {
      const marker = readInflightLetterMarker(primaryCv.user_id, jobId);
      if (!marker) return;            // No prior in-flight Generate.

      if (isInflightLetterMarkerExpired(marker)) {
        clearInflightLetterMarker(primaryCv.user_id, jobId);
        toast.info(
          "We couldn't automatically restore your last generated cover"
          + ' letter. Check Documents → Cover Letters.',
        );
        return;
      }

      // CWS-2 (operator-locked, 2026-02-15) — ID-DRIVEN RECOVERY.
      // When the marker carries a `runId` (= cover letter document
      // id), drive recovery deterministically off the canonical row
      // via `pollCoverLetterRun(runId)`. This is the polling-loop
      // upgrade described in CANDIDATE_WORKSPACE_LRO_AUDIT.md §F /
      // CWS-2. Replaces the previous single-shot
      // `findLetterByTitleStamp` which silently failed when the
      // backend write was racing the remount.
      //
      // Budget: `pollCoverLetterRun` has its own
      // POLLING_HARD_CAP_MS ceiling (~120 s) so we don't impose a
      // separate timer here. Marker TTL is checked once up front
      // (above). On any error (poll timeout / cancel / failure)
      // we fall through to the legacy title-stamp path so we never
      // regress recovery in the runId-absent / pre-CWS-2-marker case.
      let recoveredById = null;
      if (marker.runId) {
        try {
          const env = await pollCoverLetterRun(marker.runId, {
            apiGet: (path, opts) => apiFetch(path, opts),
          });
          if (cancelled) return;
          // pollCoverLetterRun returns the canonical envelope shape
          // (run_id + cover_letter_id + content_markdown + billing
          // + ...). Surface it through the SAME bind path the sync
          // success uses.
          recoveredById = {
            id:               env.cover_letter_id || env.run_id || marker.runId,
            title:            env.title || marker.title,
            content_markdown: env.content_markdown || '',
            base_cv_id:       env.base_cv_id || primaryCv.id,
            cv_variant_id:    env.cv_variant_id ?? cvVariantId,
            content_json:     env.content_json || null,
          };
        } catch (err) {
          if (cancelled) return;
          // Hard fail (status === 'failed') — surface calm copy +
          // drop the marker so subsequent mounts don't loop.
          if (err?.code === 'cover_letter_failed') {
            clearInflightLetterMarker(primaryCv.user_id, jobId);
            return;
          }
          // Timeout / transient — fall through to legacy title-stamp.
        }
      }

      // Title-stamp fallback (legacy markers without runId, or when
      // id-driven recovery couldn't terminate within its own budget).
      const found = recoveredById || await findLetterByTitleStamp(marker.title);
      if (cancelled || !found) return;

      // ── JOB-SCOPING GUARDS (workspace-only belt-and-braces) ──────
      // (a) base_cv_id must match the current primary CV.
      if (found.base_cv_id !== primaryCv.id) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Workspace CoverLetterSection] discarding recovered letter:'
          + ' base_cv_id mismatch',
          { letterBaseCv: found.base_cv_id, primaryCv: primaryCv.id },
        );
        clearInflightLetterMarker(primaryCv.user_id, jobId);
        return;
      }
      // (b) cv_variant_id must match the workspace's current
      //     carry-forward (null vs id strict equality).
      const foundVariantId = found.cv_variant_id ?? null;
      if (foundVariantId !== cvVariantId) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Workspace CoverLetterSection] discarding recovered letter:'
          + ' cv_variant_id mismatch',
          { letterVariant: foundVariantId, workspaceVariant: cvVariantId },
        );
        clearInflightLetterMarker(primaryCv.user_id, jobId);
        return;
      }
      // (c) idempotency_key must match the cached key (when present).
      const cachedKey = peekIdempotencyKey(
        primaryCv.user_id, jobId, 'cover_letter',
      );
      const letterKey = found?.content_json?.idempotency_key || null;
      if (cachedKey && letterKey && cachedKey !== letterKey) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Workspace CoverLetterSection] discarding recovered letter:'
          + ' idempotency_key mismatch',
        );
        clearInflightLetterMarker(primaryCv.user_id, jobId);
        return;
      }

      const newId = found.id || found.cover_letter_id;
      if (!newId) return;
      if (cancelled) return;

      setLetterId(newId);
      setLetterDraft(found.content_markdown || '');
      setLetterTitle(found.title || marker.title);
      setRecoveredFromInflight(true);
      clearInflightLetterMarker(primaryCv.user_id, jobId);
      // RC 1.49.5 L-2 — also drop the cached idempotency key on a
      // confirmed recovered persist so the next legitimate Generate
      // mints a fresh key.
      clearIdempotencyKey(primaryCv.user_id, jobId, 'cover_letter');
      // CWS-2 (operator-locked, 2026-02-15) — stamp the URL handle
      // so cross-tab / deep-link / back-forward restore lands
      // directly on the recovered letter (parity with IP's
      // ?interview_prep_id pattern). Best-effort; ignore if
      // setSearchParams is unavailable.
      try {
        if (typeof setSearchParams === 'function') {
          const _next = new URLSearchParams(searchParams);
          _next.set('cover_letter_id', newId);
          setSearchParams(_next, { replace: true });
        }
      } catch { /* noop */ }
      toast.success('We restored your generated cover letter.');
    })();
    return () => { cancelled = true; };
  }, [jobId, primaryCv?.user_id, primaryCv?.id, cvVariantId]);

  // ── Pre-flight credits gate (toast-only Phase 2) ─────────────────
  const ensureCreditsAvailable = (actionType, actionLabel) => {
    if (creditsLoading) return false;
    const cost = getActionCost(usageCosts, actionType);
    if (cost == null || cost === 0) return true;
    const available =
      Number(credits?.includedUsageRemaining || 0)
      + Number(credits?.extraUsageRemaining || 0);
    if (available >= cost) return true;
    toast.error(`Not enough Credits for ${actionLabel}`, {
      description: `${available} available · ${cost} needed. Open Plan to add Credits.`,
      action: {
        label: 'View plan',
        onClick: () => {
          try { window.location.assign('/account?tab=plan'); } catch { /* noop */ }
        },
      },
    });
    return false;
  };

  // ── Generate / Regenerate handler ────────────────────────────────
  // RC 1.55 P0 (2026-05-12 PM) — pending-hydration retry handler.
  // Re-invokes the canonical READ-ONLY GET on `/api/cover-letters/{id}`
  // with the same retry schedule. Never re-dispatches the LLM,
  // never re-charges. Safe to call any number of times.
  const handleRetryHydration = async () => {
    const id = pendingHydration?.letterId || letterId;
    if (!id) return;
    setRetryingHydration(true);
    try {
      const hydrated = await verifyAndHydrateCoverLetter({
        envelope: null,            // force canonical GET, skip envelope short-circuit
        letterId: id,
        apiGet:   (path, opts) => apiFetch(path, opts),
      });
      const content = hydrated?.content_markdown || hydrated?.generated_text || '';
      if (!content) {
        toast.info('Still saving. Try again in a few seconds.');
        return;
      }
      setLetterId(id);
      setLetterDraft(content);
      setLetterTitle(hydrated?.title || letterTitle || '');
      setPendingHydration(null);
      toast.success('Cover letter ready.');
    } catch (e) {
      if (e?.recoverable === true) {
        toast.info(
          'Cover letter is still saving. Please retry in a few seconds'
          + ' or open Documents → Cover Letters.',
        );
      } else {
        toast.error(e?.message || 'Could not refresh preview.');
      }
    } finally {
      setRetryingHydration(false);
    }
  };

  const handleGenerate = async (opts = {}) => {
    if (!primaryCv?.id) {
      toast.error('No Source CV. Upload one in Documents → CVs.');
      return;
    }
    if (!job?.description?.trim()) {
      toast.error('Job has no description text.');
      return;
    }
    if (!ensureCreditsAvailable('cover_letter', 'Generate cover letter')) return;

    // ── U-9.E — Regenerate confirm gate (2026-02-15) ────────────────
    //
    // OPERATOR-LOCKED. Symmetric to U-3.IP / U-5.CV.A. When a letter
    // is already bound (from this session OR from the mount-time
    // discovery hook), the click opens the confirm dialog instead
    // of dispatching. The dialog's "Regenerate cover letter" button
    // re-enters `handleGenerate({ force: true })` so this gate is
    // bypassed on confirm. First-time generation (`!letterId`) skips
    // the dialog cleanly. Read-only gate — never charges.
    if (letterId && !opts?.force) {
      setRegenerateConfirm({ source: 'cl_generate' });
      return;
    }
    // Force-regenerate path — clear the idempotency key BEFORE
    // minting a new one so the backend does not dedupe against the
    // bound letter's debit.
    if (opts?.force) {
      try {
        clearIdempotencyKey(primaryCv.user_id, jobId, 'cover_letter');
      } catch { /* noop */ }
    }

    const letterTitleNew = mintCoverLetterTitle({ company: job?.company });

    // Marker BEFORE the LLM call — so a tab close mid-Generate is
    // recoverable on next mount.
    writeInflightLetterMarker(primaryCv.user_id, jobId, letterTitleNew);
    // Get-or-mint the (user, job, action)-scoped idempotency key.
    // A retry within IDEMPOTENCY_TTL_MS reuses the SAME key so the
    // backend dedupes the second debit.
    const letterIdempotencyKey = getOrMintIdempotencyKey(
      primaryCv.user_id, jobId, 'cover_letter',
    );

    setGenerating(true);
    setProgressLabel('dispatching');
    actionTrace('cover_letter', 'dispatch_start', {
      jobId, idempotency_key: letterIdempotencyKey,
    });
    try {
      // RC 1.53 Phase 4.3A — dispatch through the
      // OperationLifecycleController. The adapter forwards the
      // existing `generateCoverLetterWithPolling` call verbatim;
      // the controller gives us:
      //   • no-op-on-in-flight guard (a second click is dropped)
      //   • STABILIZING status after the run callback resolves so
      //     the activity indicator stays mounted until ALL
      //     post-success work has landed
      //   • workspace-wide Generate-lock aggregation via
      //     isAnyRunning(jobId)
      //
      // The polling helper, idempotency-key derivation, billing
      // behaviour, and request body are UNCHANGED. RC 1.52 Phase 2
      // Hotfix history (decoupling from withTimeoutRecovery) still
      // applies — the helper owns its own async recovery; the
      // controller wraps it but does NOT re-implement recovery.
      const r = await controllerDispatch({
        jobId,
        actionCode: 'cover_letter',
        idempotencyKey: letterIdempotencyKey,
        run: async ({ callbacks }) => coverLetterAdapter.run({
          requestBody: {
            job_text:        job.description,
            // U-6.CL (2026-02-15) — stamp workspace job_id so the
            // backend `content_json.job_id` is set; powers the
            // `GET /api/cover-letters/by-job/{job_id}` lookup the
            // live binder below uses on tab-return.
            job_id:          jobId,
            title:           letterTitleNew,
            base_cv_id:      primaryCv.id,
            cv_variant_id:   cvVariantId,
            tone_type:       'analytical',
            idempotency_key: letterIdempotencyKey,
          },
          apiPost: async (path, opts) => apiFetch(path, {
            ...opts,
            body: {
              ...(opts && opts.body ? opts.body : {}),
              idempotency_key: letterIdempotencyKey,
            },
          }),
          apiGet:  (path, opts) => apiFetch(path, opts),
          // CWS-2 (operator-locked, 2026-02-15) — stamp the letter
          // id into the inflight marker the moment the async_202
          // envelope returns it. Mount recovery on a future remount
          // then drives id-based `pollCoverLetterRun(runId)` instead
          // of the fragile title-stamp search.
          onRunIdReady: (runId) => {
            try {
              patchInflightLetterMarker(primaryCv.user_id, jobId, { runId });
            } catch { /* noop */ }
          },
          onRunStatus: (s) => {
            if (s === 'running') {
              setProgressLabel('polling');
              toast.info(
                "This is taking longer than usual. We're checking your results...",
              );
            } else if (s === 'failed') {
              toast.warning(
                "We're unable to confirm the result reached you."
                + ' Before clicking Generate again, please open Documents'
                + ' → Cover Letters — your document may already be there.',
                { duration: 9000 },
              );
            }
          },
          callbacks,
        }),
      });
      // No-op replay: dispatch returned the existing terminal
      // envelope without re-invoking `run`. The section's local
      // state below still applies (it's idempotent), and finally
      // will clean up `generating`.
      if (!r) return;

      const newLetterId = r?.cover_letter_id || r?.id;
      if (!newLetterId) throw new Error('Letter id missing in response.');
      actionTrace('cover_letter', 'terminal_payload_received', {
        letterId: newLetterId,
        has_content: !!(r?.generated_text || r?.content_markdown),
      });

      // ── RC 1.55 P0 (operator-locked, 2026-05-12 PM) ─────────────
      // Post-deploy hydration race fix. The dispatch envelope `r`
      // can be thin (empty `generated_text` / `content_markdown`)
      // in the polling-helper's terminal-poll race window — the
      // durable application_documents row IS committed and the
      // charge has succeeded, but it is not yet readable at the
      // millisecond the terminal poll resolves. Previously the
      // section bound the empty draft directly, leaving the preview
      // card blank until the user tab-navigated away and back
      // (which fires the URL-rehydrate effect at L246 and reads
      // from the canonical row).
      //
      // The verifier performs an authoritative READ-ONLY GET on
      // `/api/cover-letters/{id}` with the operator-locked retry
      // schedule [0, 750, 1500, 3000] ms. It never re-dispatches
      // the LLM, never re-charges, and falls open with a calm
      // `recoverable=true` error when the row is still incomplete
      // after the full schedule — the section then surfaces the
      // existing "still generating in the background" toast and
      // exposes a retry button that only re-invokes this helper.
      setProgressLabel('hydrating');
      let hydratedRow;
      try {
        hydratedRow = await verifyAndHydrateCoverLetter({
          envelope: r,
          letterId: newLetterId,
          apiGet:   (path, opts) => apiFetch(path, opts),
        });
      } catch (hydrateErr) {
        actionTrace('cover_letter', 'post_terminal_hydrate_failed', {
          letterId: newLetterId, code: hydrateErr?.code,
        });
        // RC 1.55 P0 — calm fail-open. The row exists (charge
        // succeeded, dispatch returned an id) but the canonical
        // GET is still returning an incomplete row after the full
        // retry schedule. Surface the "Generated and saved.
        // Refreshing preview…" banner with a Retry button. The
        // section's existing recoverable toast also fires.
        setPendingHydration({ letterId: newLetterId });
        setLetterId(newLetterId);
        // Clear the idempotency marker — the artefact IS persisted
        // and any subsequent Generate click would replay it. The
        // user's retry button below re-fetches only.
        try {
          clearIdempotencyKey(primaryCv.user_id, jobId, 'cover_letter');
          clearInflightLetterMarker(primaryCv.user_id, jobId);
        } catch { /* noop */ }
        // Refresh credits so the wallet is in sync even though the
        // preview is still resolving.
        if (typeof credits?.refresh === 'function') {
          try { await credits.refresh(); } catch { /* noop */ }
        }
        cacheInvalidate(['cover-letters:all', 'dashboard:bundle']);
        toast.info(
          'Cover letter generated and saved. Refreshing preview…',
          { duration: 6000 },
        );
        try {
          lifecycleMarkComplete({
            letterId: newLetterId,
            reused: false,
            hydrationPending: true,
          });
        } catch { /* noop */ }
        return;
      }

      // The hydrator returns either the original envelope (when
      // fresh) OR the canonical row from GET. Both shapes carry
      // either `generated_text` (envelope) or `content_markdown`
      // (row); the section reads both.
      const newLetterContent =
        hydratedRow?.generated_text
        || hydratedRow?.content_markdown
        || '';

      // Drop the cached idempotency key + marker on confirmed success
      // so the next legitimate Generate click mints a fresh key.
      clearIdempotencyKey(primaryCv.user_id, jobId, 'cover_letter');
      clearInflightLetterMarker(primaryCv.user_id, jobId);

      setLetterId(newLetterId);
      // RC 1.52 Phase 3B.3 — Hotfix (operator-locked, 2026-05-11):
      // synchronous URL write inside the success handler (see
      // historical comment below the call).
      try {
        const _next = new URLSearchParams(searchParams);
        _next.set('cover_letter_id', newLetterId);
        setSearchParams(_next, { replace: true });
      } catch { /* noop — the L166 effect is a back-up */ }
      setLetterDraft(newLetterContent);
      setPendingHydration(null);
      // Title can come from either the envelope (replay/sync) or the
      // canonical row's `title` column. Prefer the envelope (matches
      // the title we just minted client-side); fall back to the row.
      setLetterTitle(
        r?.title
        || hydratedRow?.title
        || letterTitleNew,
      );
      setRecoveredFromInflight(false);
      // Billing block ONLY lives on the dispatch envelope (it is the
      // server's charging response, not a row column). The hydrated
      // row would not carry it — read from `r` only.
      setLetterBilling(
        r && typeof r.billing === 'object' ? r.billing : null,
      );
      setLetterChargeReason(r?.charge_reason ?? null);
      cacheInvalidate(['cover-letters:all', 'dashboard:bundle']);
      actionTrace('cover_letter', 'cache_invalidated', {
        keys: ['cover-letters:all', 'dashboard:bundle'],
      });
      toast.success('Cover letter generated.');
      // RC 1.53 Phase 4.3A — AWAIT credits.refresh so the activity
      // indicator stays in `stabilizing` ("Updating your
      // documents…") until the wallet pill is re-read from server.
      // The orchestrator's contract: re-read fresh state from DB,
      // never re-dispatch the LLM. Fail-open: if the refresh blips
      // we still proceed to markComplete so the indicator does not
      // get stuck.
      setProgressLabel('stabilizing');
      if (typeof credits?.refresh === 'function') {
        try { await credits.refresh(); } catch { /* noop */ }
      }
      actionTrace('cover_letter', 'credits_refresh_completed', {
        letterId: newLetterId,
      });
      // RC 1.53 Phase 4.3A — emit pack:artefact_ready so any
      // listener (Pack section) re-derives the "Application pack
      // available" gate without a manual click. Cover Letter has
      // no replay/recovery distinction at the artefact-ready
      // level — the row exists and was either freshly debited or
      // idempotent-replayed (both safe to surface).
      try {
        controllerEmit(OPERATION_EVENT_TYPES.PACK_ARTEFACT_READY, {
          jobId,
          actionCode: 'cover_letter',
          artefactId: newLetterId,
          reused: !!(r?.billing?.idempotent || r?.billing?.cached),
        });
      } catch { /* noop */ }
      setProgressLabel(null);
      // RC 1.53 Phase 4.3A — signal terminal completion to the
      // controller AFTER credits refresh + URL write + cache
      // invalidate have landed. The activity indicator unmounts
      // ONLY now — "first visible, last gone".
      try {
        lifecycleMarkComplete({
          letterId: newLetterId,
          reused: !!(r?.billing?.idempotent || r?.billing?.cached),
        });
      } catch { /* noop */ }
      actionTrace('cover_letter', 'operation_closed', {
        letterId: newLetterId,
      });
    } catch (err) {
      if (handleCreditsError(err, {
        action: 'Generate cover letter',
        refreshCredits: credits?.refresh,
      })) return;
      // RC 1.52 Phase 2 Hotfix D-3 — recoverable still-running.
      // After D-1 + D-2 ship the backend reaches terminal state in
      // ≤115s, so this branch should never fire in normal operation.
      // It is a defensive belt: if the helper's hard cap is reached
      // we surface a calm "still running, check Documents" toast
      // rather than a hard error/idle reset. NOT a substitute for
      // backend timeout enforcement — any occurrence is a bug.
      if (err && err.recoverable === true) {
        // ── U-6.CL — flip the RECOVERING surface ON so the U-4-style
        // live binder below activates immediately. Symmetric to
        // U-4.IP. Without this flip, the binder would only see the
        // SS marker — which can race the dispatch promise.
        try { setRecovering(true); } catch { /* noop */ }
        toast.info(
          'Your cover letter is still generating in the background.'
          + ' Please check Documents → Cover Letters in a minute.',
          { duration: 9000 },
        );
        return;
      }
      // RC 1.52 Phase 2 Hotfix E-2 — friendlier server-error UX.
      // Raw FastAPI 5xx surfaces as "Internal Server Error" via
      // `err.message`, which is alarming and unactionable. Backend
      // invariants guarantee no charge fires on a failed run (B2
      // release-on-exception), so we tell the user that explicitly
      // and suggest a retry. 4xx errors keep the backend's own
      // message (those are typically client-actionable: missing
      // field, plan-gate, etc.).
      const status = err?.status;
      if (typeof status === 'number' && status >= 500) {
        toast.error(
          "Sorry — we couldn't generate your cover letter just now."
          + " Your credits weren't charged. Please try again in a"
          + ' moment, or contact support if this keeps happening.',
          { duration: 9000 },
        );
      } else {
        toast.error(err?.message || 'Failed to generate cover letter.');
      }
      // RC 1.55 Phase Z.7 / G1 (operator-locked, 2026-05-13 PM) —
      // Stale inflight marker hygiene on non-recoverable terminal
      // error. Mirror of the Z.5 cv_variant pattern at
      // `CvSection.jsx::_dispatchWithCatches`. The earlier arms
      // above (handleCreditsError early-return, recoverable=true)
      // deliberately leave the marker intact: credits-error paths
      // re-dispatch via the same guards, and recoverable paths
      // mean the backend run is still in flight and should be
      // recoverable on tab-return. This arm — credits handled,
      // not recoverable, hard 4xx (non-recognised) / 5xx
      // fallthrough — is the definitive-error branch: the user
      // has been shown an actionable toast and no further
      // automatic resolution is expected. Clearing the marker
      // here prevents a stuck "Finishing your last Generate
      // attempt — no new Credits will be charged" recovery card
      // from surviving a hard failure.
      // Strict-rail: no backend / billing / scoring / idempotency
      // / finalize-charge impact — only erases the local recovery
      // hint.
      try {
        if (primaryCv?.user_id && jobId) {
          clearInflightLetterMarker(primaryCv.user_id, jobId);
        }
      } catch { /* noop — marker hygiene must never crash */ }
    } finally {
      setGenerating(false);
      setProgressLabel(null);
      // RC 1.53 Phase 4.3A — defensive controller resync. If the
      // op is still in any active state when we exit (e.g. caller
      // returned early on a recoverable toast path), surface it as
      // a controlled failure so the activity indicator unmounts.
      // `markFailed` is idempotent and a no-op when the op is
      // already in a terminal state.
      try { lifecycleMarkFailed({ message: 'generation_path_exited' }); }
      catch { /* noop */ }
    }
  };

  // ── Render helpers ───────────────────────────────────────────────
  const generateLabel = useMemo(
    () => (letterId ? 'Regenerate Cover Letter' : 'Generate Cover Letter'),
    [letterId],
  );

  // Calm carry-forward label — read-only.
  const carryForwardLabel = useMemo(() => {
    if (sourceCvType === 'cv_variant') return 'tailored CV variant';
    return primaryCv?.original_filename || 'Source CV';
  }, [sourceCvType, primaryCv?.original_filename]);

  // Preview snippet — first PREVIEW_LINES, monospace, dimmed.
  const draftPreview = useMemo(() => {
    if (!letterDraft) return '';
    return letterDraft
      .split('\n')
      .slice(0, PREVIEW_LINES)
      .join('\n');
  }, [letterDraft]);

  return (
    <div
      data-testid={rootTestId}
      className="flex flex-col gap-5 min-w-0 max-w-full"
    >
      {/* ── Header with live pricing ─────────────────────────────── */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-lg font-semibold text-ndor-ink">
            Cover Letter
          </h2>
          {/* U-9.G — Loaded-from-history chip + U-9.C — Checking
              status surface. */}
          {lookupStatus === 'checking' && (
            <span
              role="status"
              aria-live="polite"
              data-testid="workspace-letter-lookup-checking"
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-ndor-ink-muted"
            >
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              Checking for existing application materials…
            </span>
          )}
          {lookupStatus === 'ready' && loadedFromHistory && letterId && (
            <HistoryBadge testId="workspace-letter-loaded-from-history-badge" />
          )}
          {/* RC 1.53 Phase 4.3A — activity chip. First visible, last
              gone — stays mounted through stabilization (cache
              invalidate + credits refresh). */}
          <WorkspaceActivityIndicator
            jobId={jobId}
            actionCode="cover_letter"
            progressLabel={progressLabel}
          />
          {letterCostLabel != null && (
            <Badge
              className="text-[10px] uppercase tracking-[0.14em] bg-ndor-gold/15 text-ndor-gold border-0"
              data-testid="workspace-letter-price-label"
            >
              {letterCostLabel}
            </Badge>
          )}
        </div>
        <p className="text-xs text-ndor-ink-muted">
          A cover letter is optional — Continue without one anytime.
        </p>
      </div>

      {/* ── Source-CV carry-forward (read-only, calm) ──────────────── */}
      <div
        data-testid="workspace-letter-source-card"
        className="rounded-lg border border-ndor-line p-3 space-y-1"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <Mail className="h-4 w-4 text-ndor-ink-muted shrink-0" />
          <p className="text-sm font-medium text-ndor-ink truncate">
            Generated against {carryForwardLabel}
          </p>
        </div>
        <p className="text-[11px] text-ndor-ink-muted">
          Change this on the previous step if needed.
        </p>
      </div>

      {/* ── Generated / recovered letter card ───────────────────── */}
      {/* RC 1.49.7 Layer-A* UX — inline restoring banner. Renders
            ONLY while the URL-driven mount-rehydrate is in flight
            and we haven't bound a letterId yet. Clears unconditionally
            in the effect's finally block.

            RC 1.53 UX Messaging slice (2026-02): a second copy of
            this same JSX block previously lived directly below this
            one, producing a duplicate banner + duplicate
            `workspace-letter-restoring-banner` `data-testid` while
            restoring. The duplicate has been removed; this single
            block is the only restoring banner the section renders.
            ─────────────────────────────────────────────────────── */}
      {restoring && !letterId && (
        <div
          data-testid="workspace-letter-restoring-banner"
          className="rounded-lg border border-ndor-line bg-ndor-bg-subtle p-3 flex items-center gap-2"
        >
          <Loader2 className="h-4 w-4 animate-spin text-ndor-gold" aria-hidden />
          <div className="text-xs text-ndor-ink leading-relaxed">
            <p className="font-medium">Restoring your cover letter…</p>
            <p className="text-ndor-ink-muted">
              We&apos;re reading your saved letter — no Credits charged.
            </p>
          </div>
        </div>
      )}

      {letterId && (
        <div
          data-testid="workspace-letter-generated-card"
          data-cv-context-source-type={sourceCvType}
          data-cv-context-source-id={sourceCvId || ''}
          data-row-cv-variant-id={letterRowCvVariantId || ''}
          data-row-base-cv-id={letterRowBaseCvId || ''}
          className="rounded-lg border-2 border-ndor-gold/40 bg-ndor-gold/[0.02] p-3 space-y-2"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <Sparkles className="h-4 w-4 text-ndor-gold shrink-0" />
            <p className="text-sm font-medium text-ndor-ink truncate">
              {letterTitle || 'Cover letter'}
            </p>
            <Badge className="text-[10px] uppercase tracking-[0.14em] bg-ndor-gold/15 text-ndor-gold border-0">
              {recoveredFromInflight
                ? 'Recovered · just now'
                : 'Cover letter · this attempt'}
            </Badge>
            <CheckCircle2 className="h-3.5 w-3.5 text-ndor-success ml-auto" aria-hidden />
          </div>
          <p className="text-[11px] text-ndor-ink-muted leading-relaxed">
            {recoveredFromInflight
              ? 'Restored from your last Generate attempt — no new Credits charged.'
              : 'Saved to Documents → Cover Letters.'}
          </p>

          {/* ── RC 1.49.7 Slice 3 — Inline billing summary
                (operator P0, 2026-02). Render-only. Each line gated
                on a backend-supplied field. Never derives. Never
                falls back to 0 when absent — when the legacy
                pre-fix response shape arrives, the whole block
                renders as a graceful "—". ─────────────────────── */}
          {letterBilling ? (
            <p
              className={
                'text-[11px] '
                + (letterBilling.charged
                  ? 'text-ndor-warning'
                  : 'text-ndor-ink-muted')
              }
              data-testid="workspace-letter-billing-line"
            >
              {letterBilling.idempotent
                ? 'Idempotent replay — no new Credits charged.'
                : letterBilling.charged
                  ? 'Charged successfully.'
                  : 'Generated — no Credits charged.'}
              {letterBilling.usage_consumed != null && (
                <span className="ml-1">
                  · Charged{' '}
                  <span
                    className="font-mono-ui tabular-nums"
                    data-testid="workspace-letter-billing-usage-consumed"
                  >
                    {letterBilling.usage_consumed}
                  </span>
                  {' '}credit{letterBilling.usage_consumed === 1 ? '' : 's'}
                </span>
              )}
              {letterBilling.usage_source && (
                <span className="ml-1 text-ndor-ink-muted">
                  · from{' '}
                  <span
                    className="font-mono-ui"
                    data-testid="workspace-letter-billing-usage-source"
                  >
                    {letterBilling.usage_source}
                  </span>
                </span>
              )}
              {letterBilling.plan_code && (
                <span className="ml-1 text-ndor-ink-muted">
                  · plan{' '}
                  <span
                    className="font-mono-ui"
                    data-testid="workspace-letter-billing-plan-code"
                  >
                    {letterBilling.plan_code}
                  </span>
                </span>
              )}
              {letterChargeReason && letterChargeReason !== 'cover_letter_generated'
                && letterChargeReason !== 'idempotent_replay' && (
                <span className="ml-1 text-ndor-ink-muted">
                  ·{' '}
                  <span data-testid="workspace-letter-billing-reason">
                    {letterChargeReason}
                  </span>
                </span>
              )}
            </p>
          ) : (
            <p
              className="text-[11px] text-ndor-ink-muted"
              data-testid="workspace-letter-billing-unavailable"
            >
              Billing details — <span aria-hidden>—</span>{' '}
              <span className="sr-only">not available for this saved letter.</span>
            </p>
          )}

          {draftPreview && (
            <pre
              data-testid="workspace-letter-draft-preview"
              className="font-mono-ui text-[11px] text-ndor-ink-muted whitespace-pre-wrap leading-relaxed border-t border-ndor-line pt-2"
            >
              {draftPreview}
              {letterDraft.split('\n').length > PREVIEW_LINES && '\n…'}
            </pre>
          )}

          {/* RC 1.55 P0 — pending-hydration banner. Surfaced ONLY when
              the post-terminal retry schedule has exhausted (rare
              race window). The artefact is already saved + charged;
              this Retry button re-invokes the canonical READ-ONLY
              GET — never `/api/runs`, never a new charge. */}
          {pendingHydration && (
            <div
              data-testid="workspace-letter-hydration-pending"
              className="flex items-center gap-2 rounded-md border border-ndor-info-border bg-ndor-info-light/60 px-3 py-2 mt-2"
            >
              <Loader2 className="h-3.5 w-3.5 text-ndor-info shrink-0 animate-spin" />
              <p className="text-[11px] text-ndor-ink-secondary flex-1 leading-relaxed">
                Generated and saved. Refreshing preview…
              </p>
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-3 text-[11px]"
                onClick={handleRetryHydration}
                disabled={retryingHydration}
                data-testid="workspace-letter-hydration-retry-button"
              >
                {retryingHydration
                  ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  : <RefreshCw className="h-3 w-3 mr-1" />}
                Refresh preview
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ── U-9.E — CL Regenerate confirm gate (2026-02-15) ─────── */}
      {regenerateConfirm && (
        <div
          data-testid="cl-regenerate-confirm"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="cl-regenerate-confirm-title"
          className="my-4 rounded-lg border border-amber-500/60 bg-amber-500/10 p-4 text-sm"
        >
          <div
            id="cl-regenerate-confirm-title"
            className="font-medium text-amber-100 mb-1.5"
          >
            Regenerate cover letter?
          </div>
          <div className="text-amber-50/80 mb-3">
            You already have a cover letter for this job. Generating a new one
            will spend Credits and replace the version shown above.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRegenerateConfirm(null)}
              data-testid="cl-regenerate-cancel"
            >
              Keep existing
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                setRegenerateConfirm(null);
                handleGenerate({ force: true });
              }}
              disabled={generating || creditsLoading || !primaryCv?.id}
              data-testid="cl-regenerate-confirm-go"
            >
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              Regenerate cover letter
            </Button>
          </div>
        </div>
      )}

      {/* ── RC 1.49.7 Slice 4 — Two-control footer (Generate ·
            Continue). Operator P0 2026-02. The per-section "Back"
            button is REMOVED — workspace nav is forward-only.
            Backtracking is via the section-nav tabs (already-
            visited sections stay clickable) or the header
            `workspace-back-to-job` link. ──────────────────────── */}
      <footer
        data-testid="workspace-section-footer"
        /* RC 1.55 P0 Mobile pass — stack on <xs, wrap from xs+. */
        className="sticky bottom-0 z-10 -mx-3 sm:-mx-4 px-3 sm:px-4 py-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] flex flex-col xs:flex-row xs:flex-wrap xs:items-center xs:justify-between gap-2 border-t border-border/60 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80"
      >
        <Button
          variant="outline"
          size="sm"
          onClick={onBack}
          /* RC 1.53 Phase 4.2.1.1.d — navigation always free. */
          data-testid="workspace-section-back-button"
          className="w-full xs:w-auto px-2 sm:px-3"
        >
          <ArrowLeft className="mr-1 sm:mr-1.5 h-3.5 w-3.5" />
          <span className="hidden sm:inline">Back to CV</span>
          <span className="sm:hidden">Back</span>
        </Button>

        <Button
          variant="default"
          size="sm"
          onClick={handleGenerate}
          /* RC 1.53 Phase 4.2.1.1.d — workspace-wide Generate-lock.
             Phase 4.3A — also lock on the controller's active states
             so the button stays disabled through the entire
             dispatching → polling → stabilizing chain. */
          disabled={
            generating
            || creditsLoading
            || !primaryCv?.id
            || workspaceBusy
            || controllerStatus === 'dispatching'
            || controllerStatus === 'polling'
            || controllerStatus === 'stabilizing'
            /* U-9.B — lock Generate while mount-time existing-artefact
               discovery is in flight. Closes the pre-U-9 race window. */
            || lookupStatus !== 'ready'
          }
          data-testid="workspace-letter-generate-button"
          data-controller-status={controllerStatus}
          /* RC 1.55 P0 Mobile pass — grow on narrow widths. */
          className="w-full xs:w-auto xs:flex-1 xs:min-w-0 sm:flex-none px-2 sm:px-3"
        >
          {generating
            ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin shrink-0" />
            : <Sparkles className="mr-1.5 h-3.5 w-3.5 shrink-0" />}
          <span className="hidden xs:inline truncate" aria-hidden="true">
            {generateLabel}
          </span>
          <span className="xs:hidden truncate" aria-hidden="true">
            {generateLabel.startsWith('Re') ? 'Regen' : 'Generate'}
          </span>
          <span className="sr-only">{generateLabel}</span>
        </Button>

        <Button
          variant="default"
          size="sm"
          onClick={onContinue}
          /* RC 1.53 Phase 4.2.1.1.d — navigation always free. */
          data-testid="workspace-section-continue-button"
          className="w-full xs:w-auto px-2 sm:px-3"
        >
          Continue
          <ArrowRight className="ml-1 sm:ml-1.5 h-3.5 w-3.5" />
        </Button>
      </footer>

      {creditsLoading && !generating && (
        <p
          className="text-[11px] text-ndor-ink-muted"
          data-testid="workspace-letter-credits-loading-hint"
        >
          Loading your wallet…
        </p>
      )}
    </div>
  );
}
